import {
    isValidElement,
    useCallback,
    useEffect,
    useState,
    Children,
    useMemo,
    type FC
} from "react";
import {
    Platform,
    Animated,
    View
} from "react-native";
import type ISidebarProps from "./type";
import type {
    SidebarRootContextType,
    SidebarScrollStateType,
    SidebarWebPropsType
} from "./type";
import stylesheet, {
    getSidebarFrameInset,
    getSidebarMeasures,
    getSidebarGutter,
    getSidebarWidth,
    useStyles
} from "./stylesheet";
import SidebarContent from "./components/content";
import SidebarFooter from "./components/footer";
import SidebarGroup from "./components/group";
import SidebarGroupAction from "./components/groupAction";
import SidebarGroupContent from "./components/groupContent";
import SidebarGroupLabel from "./components/groupLabel";
import SidebarHeader from "./components/header";
import SidebarInput from "./components/input";
import SidebarInset from "./components/inset";
import SidebarMenu from "./components/menu";
import SidebarMenuAction from "./components/menuAction";
import SidebarMenuBadge from "./components/menuBadge";
import SidebarMenuButton from "./components/menuButton";
import SidebarMenuItem from "./components/menuItem";
import SidebarMenuSkeleton from "./components/menuSkeleton";
import SidebarMenuSub from "./components/menuSub";
import SidebarMenuSubButton from "./components/menuSubButton";
import SidebarMenuSubItem from "./components/menuSubItem";
import SidebarProvider from "./components/provider";
import SidebarRail from "./components/rail";
import SidebarSeparator from "./components/separator";
import SidebarTrigger from "./components/trigger";
import {
    SidebarRootContext,
    useSidebarContext,
    SidebarContext
} from "./context";
import useSidebar from "./hook";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme
} from "../../core/hooks";
import {
    useAnimatedState
} from "../../helpers/motion";
import Sheet from "../sheet";

const Sidebar: FC<ISidebarProps> = ({
    customLocalize: customLocalizeProp,
    customTheme: customThemeProp,
    collapseBehaviour = "icon",
    variant: variantProp,
    title: titleProp,
    side = "left",
    children,
    style,
    ...props
}) => {
    const provider = useSidebarContext({
        part: "Sidebar"
    });

    const customLocalize = customLocalizeProp ?? provider?.customLocalize;

    const customTheme = customThemeProp ?? provider?.customTheme;

    const {
        surfaceColor: parentSurfaceColor,
        controlPaddings,
        controlHeights,
        shapeConfigs,
        shadowAlpha,
        elevations,
        radiuses,
        borders,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext(customTheme);

    const {
        localize
    } = NCoreUIKitLocalize.useContext(customLocalize);

    const title = titleProp ?? localize("main-navigation");

    const variant = variantProp ?? provider?.variant ?? "sidebar";

    const isPhone = provider?.isPhone ?? false;

    const isOpen = provider?.isOpen ?? true;

    const isCollapsed = !isPhone && collapseBehaviour !== "none" && !isOpen;

    const isOffcanvasCollapsed = collapseBehaviour === "offcanvas" && isCollapsed;

    const [
        scrollState,
        setScrollState
    ] = useState<SidebarScrollStateType>({
        isOverflow: false,
        isScrolled: false
    });

    const [
        isHidden,
        setIsHidden
    ] = useState(isOffcanvasCollapsed);

    const measures = getSidebarMeasures({
        controlPaddings,
        controlHeights,
        shapeConfigs,
        spaces
    });

    const targetWidth = getSidebarWidth({
        collapseBehaviour,
        isCollapsed,
        measures,
        variant
    });

    const handleWidthFinish = () => {
        if(isOffcanvasCollapsed) {
            setIsHidden(true);
        }
    };

    const widthAnim = useAnimatedState({
        intent: isCollapsed ? "collapse" : "expand",
        isNativeDriverEnabled: false,
        onFinish: handleWidthFinish,
        value: targetWidth,
        customTheme
    });

    useEffect(() => {
        if(!isOffcanvasCollapsed) {
            setIsHidden(false);
        }
    }, [
        isOffcanvasCollapsed
    ]);

    const handleScrollState = useCallback((nextScrollState: SidebarScrollStateType) => {
        setScrollState((currentState) => {
            if(currentState.isOverflow === nextScrollState.isOverflow && currentState.isScrolled === nextScrollState.isScrolled) {
                return currentState;
            }

            return nextScrollState;
        });
    }, []);

    const frameInset = getSidebarFrameInset({
        measures,
        variant
    });

    const gutter = getSidebarGutter({
        collapseBehaviour,
        measures,
        variant
    });

    const panelOffset = widthAnim.interpolate({
        inputRange: [
            gutter,
            measures.expandedWidth + frameInset * 2
        ],
        outputRange: [
            gutter + frameInset,
            frameInset
        ]
    });

    const panelOffsetStyle = collapseBehaviour !== "offcanvas" ? null : side === "left" ? {
        end: panelOffset
    } : {
        start: panelOffset
    };

    const {
        panel: panelDynamicStyle
    } = useStyles({
        collapseBehaviour,
        shapeConfigs,
        shadowAlpha,
        elevations,
        isHidden,
        measures,
        radiuses,
        borders,
        variant,
        colors,
        side
    });

    const rootVariant = isPhone ? "sidebar" : variant;

    const rootContext = useMemo<SidebarRootContextType>(() => ({
        isOverflow: scrollState.isOverflow,
        isScrolled: scrollState.isScrolled,
        variant: rootVariant,
        collapseBehaviour,
        handleScrollState,
        customLocalize,
        customTheme,
        isCollapsed,
        isPhone,
        title,
        side
    }), [
        scrollState.isOverflow,
        scrollState.isScrolled,
        collapseBehaviour,
        handleScrollState,
        customLocalize,
        customTheme,
        isCollapsed,
        rootVariant,
        isPhone,
        title,
        side
    ]);

    const items = Children.toArray(children);

    const railElement = items.find((item) => isValidElement(item) && item.type === SidebarRail);

    const bodyElements = items.filter((item) => item !== railElement);

    const handleClose = () => {
        if(provider?.isOpen) {
            provider.handleOpenChange(false);
        }
    };

    const handleSheetOpenChange = (isNextOpen: boolean) => {
        if(!isNextOpen) {
            handleClose();
        }
    };

    const webProps: SidebarWebPropsType = Platform.OS === "web" ? {
        dataSet: {
            state: isCollapsed ? "collapsed" : "expanded",
            collapsible: collapseBehaviour
        }
    } : {};

    if(isPhone) {
        return <Sheet
            onOpenChange={handleSheetOpenChange}
            width={shapeConfigs.drawerWidth}
            customLocalize={customLocalize}
            compactBehaviour="keepSide"
            customTheme={customTheme}
            isOpen={isOpen}
            side={side}
        >
            <Sheet.Content
                aria-label={title}
            >
                <SidebarContext.Provider
                    value={provider}
                >
                    <SidebarRootContext.Provider
                        value={rootContext}
                    >
                        <View
                            style={stylesheet.phone}
                        >
                            {bodyElements}
                        </View>
                    </SidebarRootContext.Provider>
                </SidebarContext.Provider>
            </Sheet.Content>
        </Sheet>;
    }

    return <SidebarRootContext.Provider
        value={rootContext}
    >
        <Animated.View
            {...webProps}
            {...props}
            style={[
                style,
                stylesheet.container,
                {
                    width: widthAnim
                }
            ]}
            id={provider?.sidebarId}
        >
            <Animated.View
                style={[
                    stylesheet.clip,
                    panelDynamicStyle,
                    panelOffsetStyle
                ]}
            >
                <NCoreUIKitTheme.SurfaceProvider
                    surfaceColor={variant === "floating" ? parentSurfaceColor : "mid"}
                >
                    {bodyElements}
                </NCoreUIKitTheme.SurfaceProvider>
            </Animated.View>
            {railElement}
        </Animated.View>
    </SidebarRootContext.Provider>;
};
export default Object.assign(Sidebar, {
    MenuSubButton: SidebarMenuSubButton,
    GroupContent: SidebarGroupContent,
    MenuSkeleton: SidebarMenuSkeleton,
    GroupAction: SidebarGroupAction,
    MenuSubItem: SidebarMenuSubItem,
    GroupLabel: SidebarGroupLabel,
    MenuAction: SidebarMenuAction,
    MenuButton: SidebarMenuButton,
    MenuBadge: SidebarMenuBadge,
    Separator: SidebarSeparator,
    MenuItem: SidebarMenuItem,
    Provider: SidebarProvider,
    Content: SidebarContent,
    MenuSub: SidebarMenuSub,
    Trigger: SidebarTrigger,
    useContext: useSidebar,
    Footer: SidebarFooter,
    Header: SidebarHeader,
    Group: SidebarGroup,
    Input: SidebarInput,
    Inset: SidebarInset,
    Menu: SidebarMenu,
    Rail: SidebarRail
});
